/**
 * 路由表 — Phase E.3-1 实装
 * 对齐 E.0 §4 路由表设计
 *
 * 路由：/ /products /order/:id /pay/:id /ad-fullscreen /maintenance /error/:code
 *       /verify/:token（公开，无需鉴权，用于核销二维码扫码后的验证页）
 *
 * F.4：idle_to_ad 埋点在闲置定时器触发跳转时上报
 */

import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useMachineStore } from '@/stores/machineStore'

const routes: RouteRecordRaw[] = [
  // 首页
  {
    path: '/',
    name: 'home',
    component: () => import('@/pages/HomePage.vue'),
    meta: { layout: 'default' },
  },
  // 商品列表
  {
    path: '/products',
    name: 'products',
    component: () => import('@/pages/ProductListPage.vue'),
    meta: { layout: 'default' },
  },
  // 订单详情
  {
    path: '/order/:id',
    name: 'order-detail',
    component: () => import('@/pages/OrderDetailPage.vue'),
    meta: { layout: 'default' },
    props: true,
  },
  // 支付页（E.3-2 正式实装）
  {
    path: '/pay/:id',
    name: 'pay',
    component: () => import('@/pages/PaymentPage.vue'),
    meta: { layout: 'default' },
    props: true,
  },
  // 广告全屏轮播
  {
    path: '/ad-fullscreen',
    name: 'ad-fullscreen',
    component: () => import('@/pages/AdCarouselPage.vue'),
    meta: { layout: 'fullscreen' },
  },
  // 维护页
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/pages/MaintenancePage.vue'),
    meta: { layout: 'fullscreen' },
  },
  // 错误页
  {
    path: '/error/:code',
    name: 'error',
    component: () => import('@/pages/ErrorPage.vue'),
    meta: { layout: 'fullscreen' },
    props: true,
  },
  // 核销验证页（公开，无需 kiosk 鉴权，供客户扫码查看兑换状态）
  {
    path: '/verify/:token',
    name: 'verify',
    component: () => import('@/pages/VerifyPage.vue'),
    meta: { layout: 'fullscreen', public: true },
    props: true,
  },
  // 404 兜底
  {
    path: '/:pathMatch(.*)*',
    redirect: '/error/not_found',
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// ── 路由守卫 ────────────────────────────────────────────────────────────────

router.beforeEach((to, from) => {
  const machine = useMachineStore()

  // 公开路由（meta.public = true）：跳过所有 kiosk 守卫
  if (to.meta['public'] === true) {
    return true
  }

  // 维护态守卫：非维护页一律重定向
  if (machine.maintenanceMode && to.name !== 'maintenance') {
    return { name: 'maintenance' }
  }

  // 锁定态守卫（lock 远程指令）：非维护页一律重定向
  if (machine.isLocked && to.name !== 'maintenance') {
    return { name: 'maintenance' }
  }

  // 机器码未设置守卫（生产环境）
  // 运维设置页必须放行：新装机台需先进运维粘贴機器碼（否则鸡生蛋）
  if (
    !machine.code &&
    to.name !== 'home' &&
    to.name !== 'error' &&
    to.name !== 'admin-settings' &&
    import.meta.env.PROD
  ) {
    return { name: 'error', params: { code: 'no_machine_code' } }
  }

  // 进入广告全屏前，保存来源路由，退出时可恢复
  if (to.name === 'ad-fullscreen' && from.name && from.name !== 'ad-fullscreen') {
    sessionStorage.setItem('adReturnPath', from.fullPath)
  }

  return true
})

// ── 闲置 180s 自动进广告全屏 ────────────────────────────────────────────────

let idleTimer: ReturnType<typeof setTimeout> | null = null

function resetIdle() {
  if (idleTimer !== null) clearTimeout(idleTimer)
  idleTimer = setTimeout(() => {
    const current = router.currentRoute.value.name
    if (current !== 'ad-fullscreen' && current !== 'maintenance') {
      // F.4 idle_to_ad 埋点：闲置触发跳广告全屏
      const fromRoute = String(current ?? 'unknown')
      import('@/composables/useTelemetry').then(({ getTelemetry }) => {
        getTelemetry().track('idle_to_ad', {
          from_route: fromRoute,
          idle_seconds: 180,
        })
      }).catch(() => { /* 静默 */ })
      void router.push('/ad-fullscreen')
    }
  }, 180_000)
}

// 页面交互重置闲置计时
;['click', 'touchstart', 'keydown'].forEach((evt) =>
  window.addEventListener(evt, resetIdle),
)

// 路由跳转后也重置
router.afterEach(() => resetIdle())

// afterEach 日志（仅记录路由名，不记录敏感信息）
router.afterEach((to) => {
  if (import.meta.env.DEV) {
    console.warn(`[Router] navigate → ${String(to.name)} (${to.path})`)
  }
})
